import { useState, useEffect } from 'react';
import { supabase } from '../config/supabaseClient';

export interface PartsLookupOrgData {
  organization: {
    id: number;
    name: string;
    email: string;
    tier: string | null;
  };
  organizationSettings: {
    primary_color: string | null;
  } | null;
  organizationPhoneNumber: {
    phone_number: string;
  } | null;
}

interface UsePartsLookupOrgDataResult {
  data: PartsLookupOrgData | null;
  loading: boolean;
  error: string | null;
}

export function usePartsLookupOrgData(organizationId: string | undefined): UsePartsLookupOrgDataResult {
  const [data, setData] = useState<PartsLookupOrgData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!organizationId) {
      setError("Organization ID is missing.");
      setData(null);
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      setData(null);

      try {
        const orgIdNumber = parseInt(organizationId, 10);
        if (isNaN(orgIdNumber)) {
          throw new Error("Invalid Organization ID format.");
        }

        const [orgResponse, settingsResponse, phoneNumberResponse] = await Promise.all([
          supabase.from('organizations').select('id, name, email, tier').eq('id', orgIdNumber).maybeSingle(),
          supabase.from('organization_public_branding').select('primary_color').eq('organization_id', orgIdNumber).maybeSingle(),
          supabase.from('organization_phone_numbers').select('phone_number').eq('organization_id', orgIdNumber).eq('is_primary', true).maybeSingle()
        ]);

        const errors = [orgResponse.error, settingsResponse.error, phoneNumberResponse.error].filter(Boolean);
        if (errors.length > 0) {
          throw new Error(`Failed to fetch data: ${errors[0]?.message || 'Unknown error'}`);
        }

        if (!orgResponse.data) {
          throw new Error(`Organization with ID ${organizationId} not found.`);
        }

        setData({
          organization: orgResponse.data,
          organizationSettings: settingsResponse.data || null,
          organizationPhoneNumber: phoneNumberResponse.data || null
        });
      } catch (err: any) {
        console.error("Error in usePartsLookupOrgData hook:", err);
        setError(err.message || "An unexpected error occurred.");
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [organizationId]);

  return { data, loading, error };
}
